import { DynamicStyleSheetService, IDynamicStyleSheet } from '@captiveaire/dynamicstylesheet';
import { CommonBroadcastService } from '@caslink/common-client/ng-app/common-broadcast/common-broadcast.service';
import * as angular from 'angular';
import { Subscription } from 'rxjs/internal/Subscription';
import { AppRootScope } from '../typings/AppRootScope';
import { EventTypes } from '../typings/models';
import { IScrollbarService } from '../typings/services';

/* A virtual repeater table should either:
 * a) be the last item on the page & given the expand-to-fit attr
 * b) be given a static height from somewhere else
 */

export class VirtualRepeaterTableComponent implements angular.IComponentOptions {
    public static $name: string = 'virtualRepeaterTable';
    public bindings: any = {};
    public controller = VirtualRepeaterTableController;
    public bindToController: boolean = true;

    public template = ['$attrs', ($attrs: angular.IAttributes) => {
        return `
                <div class="cl-sensor-data-view">
                    <div class="cl-sensor-data-toolbar" ng-transclude="toolbar"></div>
                    <div class="cl-sensor-data-header" ng-transclude="header"></div>
                    <div class="cl-sensor-data-table">
                        <div layout-fill ng-transclude="table" ng-if="$ctrl.render"></div>
                    </div>
                </div>
            `;
    }];

    public transclude: any = {
        header: 'virtualRepeaterHeader',
        table: 'virtualRepeaterBody',
        toolbar: 'virtualRepeaterToolbar'
    };
}

// tslint:disable-next-line: max-classes-per-file
export class VirtualRepeaterTableController implements angular.IComponentController {
    public static $inject: string[] = [
        '$attrs',
        '$element',
        '$mdMedia',
        '$scope',
        '$timeout',
        'dynamicStyleSheetService',
        'scrollbarService',
        'commonBroadcastService',
    ];

    private render: boolean = false;
    private dynamicStyleSheets: { [name: string]: IDynamicStyleSheet } = {};
    private subscription: Subscription;

    constructor(
        private $attrs: angular.IAttributes,
        private $element: angular.IAugmentedJQuery,
        private $mdMedia: angular.material.IMedia,
        private $scope: AppRootScope,
        private $timeout: angular.ITimeoutService,
        private dynamicStyleSheetService: DynamicStyleSheetService,
        private scrollbarService: IScrollbarService,
        private commonBroadcastService: CommonBroadcastService,

    ) {
    }

    $postLink() {
        this.setup();

        this.subscription = this.commonBroadcastService.on(EventTypes.IssueRefreshEvent)
            .subscribe(() => this.setup());
    }

    $onDestroy() {
        this.subscription?.unsubscribe();
    }

    // tslint:disable-next-line: no-empty
    private removeSizerWatcher: () => void = () => { };

    private getAdjustment() {
        let adjustments = [
            this.$element.find('.cl-sensor-data-toolbar').outerHeight()
        ];

        return adjustments.reduce((a, b) => a += b);
    }

    private setup() {
        // Remove the previous one since we'll want to start watching a new element
        this.removeSizerWatcher();

        // We need to know the height of the new rows BEFORE angular-material starts rebuilding the table.
        this.resizeRows();

        // Then, destroy the table
        this.render = false;

        // Allow a $digest cycle so the table is actually removed
        this.$timeout(() => {
            // Adjust the container
            this.setHeight();

            // Allow angular to re-render the table
            this.render = true;

            // Start watching the size of the sizer so we know when to apply the scrollbar fix.
            this.removeSizerWatcher = this.watchSizer();
        });
    }

    private setHeight() {
        /* We may want to set an explicit height sometimes */
        if (!('expandToFit' in this.$attrs)) {
            return;
        }

        let $element = this.$element;

        let pageHeight: number = angular.element(window).height();
        let offset: number = $element.offset().top;

        let adjust = this.getAdjustment();

        let finalHeight = pageHeight - offset - adjust;

        $element.height(finalHeight);
    }

    private applyScrollbarFix(sizerHeight: number) {
        let $element = this.$element;

        let scrollbarWidth: number = this.scrollbarService.getScrollbarSize();
        let elemHeight = $element.outerHeight();

        let $header = $element.find('.cl-sensor-data-header');
        let $headerRow = $element.find('.cl-sensor-header-row');

        /* If this is true, we are overflowed and should adjust the padding on the header bar. */
        let isOverflowed = sizerHeight > (elemHeight - $header.height());

        $header.css('paddingRight', isOverflowed ? scrollbarWidth : 0);
        $headerRow.css('width', isOverflowed ? 'calc(100% - ' + scrollbarWidth + 'px)' : '100%');
    }

    private resizeRows() {
        let $element = this.$element;
        let height = $element.outerHeight();

        /* Font size will be set in CSS */
        let $virtualRepeaterBody = $element.find('.cl-sensor-data-table');
        let fontSize = parseInt($virtualRepeaterBody.css('font-size'), 10);

        let elementName = this.$attrs['id'];
        let dynamicStyleSheetName = elementName + '-dynamic-style';

        /**
         * Each repeater needs its own dynamic style sheet for storing the current row height for
         * angular-material to be able to compute heights correctly
         */
        this.dynamicStyleSheets[dynamicStyleSheetName] = this.dynamicStyleSheetService.getOrCreate(dynamicStyleSheetName);

        /* We'll need to clear the styles from last time so the new ones actually get applied + don't waste memory */
        this.dynamicStyleSheets[dynamicStyleSheetName].clearRules();

        let newHeight: number;

        /**
         * We need our virtual repeaters to be responsive, so we'll set a new height on the data rows,
         * calculated by the number of items in the row, if we're on a small screen
         * This should be done before other calculations
         * to make sure the contentHeight is calculated correctly
         */
        if (!this.$mdMedia('gt-sm')) {
            /*
             * We'll use the header in order to calculate the number of children.
             * Make sure that the header & the table always have the same number.
             * We can't rely on a data row since angular may not have rendered it by the time this function executes
             */
            let $virtualRepeaterHeader = $element.find('virtual-repeater-header');
            let $children = $virtualRepeaterHeader.children();

            // tslint:disable-next-line: no-magic-numbers
            newHeight = fontSize * $children.length * 2;
        } else {
            // tslint:disable-next-line: no-magic-numbers
            newHeight = fontSize * 3;
        }

        this.dynamicStyleSheets[dynamicStyleSheetName]
            .addRule('#' + elementName + ' virtual-repeater-row', 'min-height: ' + newHeight + 'px;')
            .addRule('#' + elementName + ' .cl-sensor-category-row', 'line-height: ' + newHeight + 'px;');
    }

    private watchSizer() {
        return this.$scope.$watch(() => this.$element.find('.md-virtual-repeat-sizer').css('height'),
            (newHeight: string, oldHeight: string) => {
                if (newHeight !== oldHeight) {
                    this.applyScrollbarFix(parseInt(newHeight, 10));
                }
            });
    }
}
